/**
 * 배포 스큐(deployment skew) 회복 — 오래된 탭이 사라진 서버 액션을 부를 때. (#271)
 *
 * 배경: Next 는 서버 액션 ID 를 빌드별 암호화 키로 salt 해서 만든다. 키를 고정하지
 * 않으면 배포마다 ID 가 바뀌고, 그 전에 열려 있던 탭의 액션 호출은 서버에서
 * `Failed to find Server Action` 으로 끝난다. 이때 응답은 200 인데 액션 결과가 없어
 * **대기 중이던 프로미스가 `undefined` 로 resolve** 된다(예외가 아니다).
 *
 * 1차 대책은 키 고정(`NEXT_SERVER_ACTIONS_ENCRYPTION_KEY`)이지만, 액션 파일 자체가
 * 바뀌면 ID 는 여전히 바뀐다. 그 잔여 경우를 여기서 처리한다 — 입력한 글을 살려 두고
 * 페이지를 한 번 새로 불러온 뒤 되돌려 준다.
 */

const PENDING_TEXT_PREFIX = 'stale-deploy:pending-text:';
const RELOADED_AT_KEY = 'stale-deploy:reloaded-at';

/** 같은 탭에서 이 시간 안에는 다시 새로고침하지 않는다 — 무한 새로고침 방지. */
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

/** 안내 토스트를 읽을 시간. */
const RELOAD_DELAY_MS = 1500;

/** 페이지별로 따로 보관한다 — /correct 의 글이 /analyze 에 되살아나면 안 된다. */
function pendingTextKey(): string {
  return PENDING_TEXT_PREFIX + window.location.pathname;
}

/**
 * 배포 스큐로 판단되는 상황에서 입력을 보존하고 페이지를 새로 불러옵니다.
 *
 * @returns 새로고침을 예약했으면 true. 쿨다운에 걸렸거나 sessionStorage 를 쓸 수
 *   없으면(시크릿 모드 등) false — 호출자는 일반 오류 안내로 넘어가야 한다.
 */
export function recoverFromStaleDeployment(text: string): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const lastReloadedAt = Number(
      window.sessionStorage.getItem(RELOADED_AT_KEY) ?? 0
    );
    if (Date.now() - lastReloadedAt < RELOAD_COOLDOWN_MS) return false;

    window.sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
    if (text.trim()) {
      window.sessionStorage.setItem(pendingTextKey(), text);
    }
  } catch {
    return false;
  }

  window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
  return true;
}

/**
 * 마지막 입력을 페이지 경로와 함께 메모리에 기억해 둔다.
 *
 * 오류 바운더리(#273 청크 회복)는 에디터 상태에 접근할 수 없다 — 오류가 난 시점에는
 * 컴포넌트 트리가 이미 무너져 있다. 그래서 에디터가 입력할 때마다 여기 미리 얹어 두고,
 * 새로고침 직전에 `stashTrackedText` 가 sessionStorage 로 옮긴다.
 */
let trackedText: { path: string; text: string } | null = null;

export function trackPendingText(text: string): void {
  if (typeof window === 'undefined') return;
  trackedText = { path: window.location.pathname, text };
}

/**
 * 추적해 둔 입력을 sessionStorage 에 보존합니다 — 새로고침 직전에 부른다. (#273)
 * 저장에 실패해도 조용히 넘어간다. 입력 보존보다 회복(새로고침)이 우선이다.
 */
export function stashTrackedText(): void {
  try {
    if (!trackedText?.text.trim()) return;
    window.sessionStorage.setItem(
      PENDING_TEXT_PREFIX + trackedText.path,
      trackedText.text
    );
  } catch {
    /* noop */
  }
}

/**
 * 새로고침 전에 보존해 둔 입력을 꺼내 옵니다(한 번만 — 꺼내면 지웁니다).
 */
export function takePendingText(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const key = pendingTextKey();
    const text = window.sessionStorage.getItem(key);
    if (text) window.sessionStorage.removeItem(key);
    return text;
  } catch {
    return null;
  }
}

/**
 * 사용자가 스스로 요청을 끊은 경우인지 판별합니다(탭 이동·페이지 이탈).
 * 이때는 안내를 띄우지 않는다 — 사용자가 이미 떠났고, 토스트는 노이즈다.
 */
export function isRequestAborted(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === 'AbortError';
}

/** 브라우저가 오프라인이거나 요청이 네트워크 단계에서 끊긴 경우. */
export function isNetworkFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  const message = String(
    (error as { message?: string } | null)?.message ?? error ?? ''
  );
  return /Failed to fetch|Load failed|NetworkError|network error/i.test(message);
}
